import { errorUtils } from "@/utils/error";

// GA4 docs:
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events

export interface GAItem {
  item_id: number | string;
  item_name: string;
  quantity: number;
  price: number;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

type GtagFunction = NonNullable<Window["gtag"]>

function waitForGtag(timeout: number = 50000): Promise<GtagFunction> {
  return new Promise((resolve, reject) => {
    const interval = 100;
    let elapsed = 0;

    const timer = setInterval(() => {
      if (typeof window.gtag === "function") {
        clearInterval(timer);
        resolve(window.gtag);
      } else {
        elapsed += interval;
        if (elapsed >= timeout) {
          clearInterval(timer);
          reject(new Error("gtag not loaded within timeout"));
        }
      }
    }, interval);
  });
}

export const gaAddToCart = async (
  currency: string = "AUD",
  value: number,
  productGroup: {
    productID: number | string;
    productName: string;
    quantity: number;
    price: number;
  }[],
): Promise<void> => {
  try {
    await waitForGtag();

    const items: GAItem[] = productGroup.map((item) => ({
      item_id: item.productID,
      item_name: item.productName,
      quantity: item.quantity,
      price: item.price,
    }));

    window.gtag?.("event", "add_to_cart", {
      currency,
      value,
      items,
    });
  } catch (error: unknown) {
    console.log(errorUtils.getErrorMessage(error));
  }
};

export interface GAPurchaseParams {
  currency?: string;
  transaction_id: string;
  value: number;
  coupon?: string;
  shipping?: number;
  tax?: number;
  items: GAItem[];
}

export const gaPurchase = async ({
  currency = "AUD",
  transaction_id,
  value,
  coupon = "",
  shipping = 0,
  tax = 0,
  items,
}: GAPurchaseParams): Promise<void> => {
  try {
    await waitForGtag();

    window.gtag?.("event", "purchase", {
      currency,
      transaction_id,
      value,
      coupon,
      shipping,
      tax,
      items,
    });
  } catch (error: unknown) {
    console.log(errorUtils.getErrorMessage(error));
  }
};

export interface GABeginCheckoutParams {
  currency?: string;
  value: number;
  items: GAItem[];
}

export const gaBeginCheckout = async ({
  currency = "AUD",
  value,
  items,
}: GABeginCheckoutParams): Promise<void> => {
  try {
    await waitForGtag();

    window.gtag?.("event", "begin_checkout", {
      currency,
      value,
      items,
    });
  } catch (error: unknown) {
    console.log(errorUtils.getErrorMessage(error));
  }
};
